import classnames from "classnames";
import React, {
    Fragment,
    useContext,
    useEffect,
    useRef,
    useState,
} from "react";

import { useOutsideAlerter } from "@utils/hooks/useOutsideAlerter";

import { FlyoutCategory } from "@redux/modules/value/flyoutCategories/flyoutCategories.types";

import { MenuContext } from "@context/MenuContext";

import "./flyoutMenu.scss";

type Props = {
    active?: boolean;
    data?: FlyoutCategory;
    update: (overwrite?: boolean) => void;
};

type State = {
    activeCategory: number;
    activeCategory2: number | null;
    activeCategory3: number | null;
    activeCategory4: number | null;
};

const Category = ({
    data,
    activeCategory,
    handleMouseEnter,
    hasTitle,
    noBorder,
}: {
    data?: FlyoutCategory;
    activeCategory: number | null;
    handleMouseEnter: (index: number) => void;
    hasTitle?: boolean;
    noBorder?: boolean;
}) => {
    const menuElement: any = useRef(null);
    const mouseEnterTimeout = useRef();
    const { getActivationDelay } = useContext(MenuContext);

    useEffect(() => {
        return () => {
            if (mouseEnterTimeout.current) {
                clearTimeout(mouseEnterTimeout.current);
            }
        };
    }, []);

    const onMouseEnter = (index: number) => {
        if (mouseEnterTimeout.current) {
            clearTimeout(mouseEnterTimeout.current);
        }

        // @ts-ignore
        const activationDelay = getActivationDelay(
            menuElement.current,
            activeCategory!,
        );

        if (activationDelay) {
            // @ts-ignore
            mouseEnterTimeout.current = setTimeout(() => {
                handleMouseEnter(index);
            }, activationDelay);
        } else {
            handleMouseEnter(index);
        }
    };

    const onMouseLeave = () => {
        if (mouseEnterTimeout.current) {
            clearTimeout(mouseEnterTimeout.current);
        }
    };

    return (
        <div
            ref={menuElement}
            className={classnames(
                "flyout-menu__col",
                { ["flyout-menu__col--border"]: !noBorder },
                "col-3",
            )}
        >
            {hasTitle && data && (data.subCategories || []).length > 0 && (
                <h3 className="btn-link">
                    <a href={data.url}>{data.title}</a>
                </h3>
            )}
            <ul className="flyout-menu__list">
                {data &&
                    data.subCategories.map((category: any, index: number) => (
                        <li
                            key={`${category.id}--${index}`}
                            onMouseEnter={() => onMouseEnter(index)}
                            onMouseLeave={() => onMouseLeave()}
                            className={classnames(
                                "btn-link",
                                "flyout-menu__list__link",
                                activeCategory === index
                                    ? "flyout-menu__list__link__active"
                                    : "",
                            )}
                        >
                            {activeCategory === index ? (
                                <a href={category.url}>{category.title}</a>
                            ) : (
                                <a href="#">{category.title}</a>
                            )}
                        </li>
                    ))}
            </ul>
        </div>
    );
};

const FlyoutMenu = ({ active = false, data, update }: Props) => {
    const [activeCategories, setActiveCategories] = useState<State>({
        activeCategory: 0,
        activeCategory2: null,
        activeCategory3: null,
        activeCategory4: null,
    });
    const ref = useRef<HTMLDivElement | null>(null);

    useOutsideAlerter(
        () => {
            update(false);
        },
        ref,
        ["flyout-button__toggle", "flyout-button__chevron"],
    );

    const {
        activeCategory,
        activeCategory2,
        activeCategory3,
        activeCategory4,
    } = activeCategories;

    return (
        <Fragment>
            {active && (
                <div className="w-100 d-flex justify-content-center">
                    <div className={"flyout-menu container"} ref={ref}>
                        <div className="flyout-menu__content">
                            <div className="flyout-menu__row row">
                                <Category
                                    data={data}
                                    activeCategory={activeCategory}
                                    handleMouseEnter={index =>
                                        setActiveCategories({
                                            activeCategory: index,
                                            activeCategory2: null,
                                            activeCategory3: null,
                                            activeCategory4: null,
                                        })
                                    }
                                />
                                <Category
                                    hasTitle
                                    data={data?.subCategories[activeCategory]}
                                    activeCategory={activeCategory2}
                                    handleMouseEnter={index =>
                                        setActiveCategories({
                                            activeCategory,
                                            activeCategory2: index,
                                            activeCategory3: null,
                                            activeCategory4: null,
                                        })
                                    }
                                />
                                <Category
                                    hasTitle
                                    data={
                                        data?.subCategories[activeCategory] &&
                                        activeCategory2 !== null
                                            ? data?.subCategories[
                                                  activeCategory
                                              ].subCategories[activeCategory2]
                                            : undefined
                                    }
                                    activeCategory={activeCategory3}
                                    handleMouseEnter={index =>
                                        setActiveCategories({
                                            activeCategory,
                                            activeCategory2,
                                            activeCategory3: index,
                                            activeCategory4: null,
                                        })
                                    }
                                />
                                <Category
                                    hasTitle
                                    data={
                                        activeCategory2 !== null &&
                                        data?.subCategories[activeCategory]
                                            .subCategories[activeCategory2] &&
                                        activeCategory3 !== null
                                            ? data.subCategories[activeCategory]
                                                  .subCategories[
                                                  activeCategory2
                                              ].subCategories[activeCategory3]
                                            : undefined
                                    }
                                    activeCategory={activeCategory4}
                                    handleMouseEnter={index =>
                                        setActiveCategories({
                                            activeCategory,
                                            activeCategory2,
                                            activeCategory3,
                                            activeCategory4: index,
                                        })
                                    }
                                    noBorder
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <div
                onClick={() => update()}
                className={classnames(
                    "flyout-menu__lightbox",
                    active && "modal-backdrop fade show",
                )}
            />
        </Fragment>
    );
};

export default FlyoutMenu;
